import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Pagination,
  Stack
} from '@mui/material'
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Settings as SettingsIcon
} from '@mui/icons-material'
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore'
import { db } from '../../firebase'

interface PaymentMethod {
  id: string
  name: string
  type: 'credit_card' | 'bank_transfer' | 'wallet' | 'cash_on_delivery'
  isActive: boolean
  fees: number
  minAmount: number
  maxAmount: number
  description: string
  icon: string
  createdAt: any
}

interface PaymentGateway {
  id: string
  name: string
  provider: string
  isActive: boolean
  apiKey: string
  secretKey: string
  webhookUrl: string
  supportedMethods: string[]
  createdAt: any
}

export default function PaymentsPage() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway[]>([])
  const [loading, setLoading] = useState(true)
  const [methodDialogOpen, setMethodDialogOpen] = useState(false)
  const [gatewayDialogOpen, setGatewayDialogOpen] = useState(false)
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null)
  const [editingGateway, setEditingGateway] = useState<PaymentGateway | null>(null)

  // Form states for payment methods
  const [methodName, setMethodName] = useState('')
  const [methodType, setMethodType] = useState<'credit_card' | 'bank_transfer' | 'wallet' | 'cash_on_delivery'>('credit_card')
  const [methodFees, setMethodFees] = useState('')
  const [methodMinAmount, setMethodMinAmount] = useState('')
  const [methodMaxAmount, setMethodMaxAmount] = useState('')
  const [methodDescription, setMethodDescription] = useState('')
  const [methodIsActive, setMethodIsActive] = useState(true)
  const [methodsPage, setMethodsPage] = useState(1)
  const [gatewaysPage, setGatewaysPage] = useState(1)
  const [pageSize] = useState(10)

  // Form states for payment gateways
  const [gatewayName, setGatewayName] = useState('')
  const [gatewayProvider, setGatewayProvider] = useState('')
  const [gatewayApiKey, setGatewayApiKey] = useState('')
  const [gatewaySecretKey, setGatewaySecretKey] = useState('')
  const [gatewayWebhookUrl, setGatewayWebhookUrl] = useState('')
  const [gatewayIsActive, setGatewayIsActive] = useState(true)

  useEffect(() => {
    loadPaymentMethods()
    loadPaymentGateways()
  }, [])

  const loadPaymentMethods = async () => {
    try {
      const methodsSnapshot = await getDocs(collection(db, 'payment_methods'))
      const methodsData = methodsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PaymentMethod[]
      setPaymentMethods(methodsData)
    } catch (error) {
      console.error('Error loading payment methods:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadPaymentGateways = async () => {
    try {
      const gatewaysSnapshot = await getDocs(collection(db, 'payment_gateways'))
      const gatewaysData = gatewaysSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PaymentGateway[]
      setPaymentGateways(gatewaysData)
    } catch (error) {
      console.error('Error loading payment gateways:', error)
    }
  }

  const handleSaveMethod = async () => {
    try {
      const methodData = {
        name: methodName,
        type: methodType,
        isActive: methodIsActive,
        fees: parseFloat(methodFees) || 0,
        minAmount: parseFloat(methodMinAmount) || 0,
        maxAmount: parseFloat(methodMaxAmount) || 10000,
        description: methodDescription,
        icon: getMethodIcon(methodType),
        createdAt: editingMethod ? editingMethod.createdAt : new Date()
      }

      if (editingMethod) {
        await updateDoc(doc(db, 'payment_methods', editingMethod.id), methodData)
      } else {
        await addDoc(collection(db, 'payment_methods'), methodData)
      }

      setMethodDialogOpen(false)
      resetMethodForm()
      loadPaymentMethods()
    } catch (error) {
      console.error('Error saving payment method:', error)
    }
  }

  const handleSaveGateway = async () => {
    try {
      const gatewayData = {
        name: gatewayName,
        provider: gatewayProvider,
        isActive: gatewayIsActive,
        apiKey: gatewayApiKey,
        secretKey: gatewaySecretKey,
        webhookUrl: gatewayWebhookUrl,
        supportedMethods: [methodType],
        createdAt: editingGateway ? editingGateway.createdAt : new Date()
      }

      if (editingGateway) {
        await updateDoc(doc(db, 'payment_gateways', editingGateway.id), gatewayData)
      } else {
        await addDoc(collection(db, 'payment_gateways'), gatewayData)
      }

      setGatewayDialogOpen(false)
      resetGatewayForm()
      loadPaymentGateways()
    } catch (error) {
      console.error('Error saving payment gateway:', error)
    }
  }

  const resetMethodForm = () => {
    setMethodName('')
    setMethodType('credit_card')
    setMethodFees('')
    setMethodMinAmount('')
    setMethodMaxAmount('')
    setMethodDescription('')
    setMethodIsActive(true)
    setEditingMethod(null)
  }

  const resetGatewayForm = () => {
    setGatewayName('')
    setGatewayProvider('')
    setGatewayApiKey('')
    setGatewaySecretKey('')
    setGatewayWebhookUrl('')
    setGatewayIsActive(true)
    setEditingGateway(null)
  }

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'credit_card': return '💳'
      case 'bank_transfer': return '🏦'
      case 'wallet': return '👛'
      case 'cash_on_delivery': return '💰'
      default: return '💳'
    }
  }

  const getMethodTypeText = (type: string) => {
    switch (type) {
      case 'credit_card': return 'بطاقة ائتمان'
      case 'bank_transfer': return 'تحويل بنكي'
      case 'wallet': return 'محفظة إلكترونية'
      case 'cash_on_delivery': return 'الدفع عند الاستلام'
      default: return 'غير معروف'
    }
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error'
  }

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'نشط' : 'غير نشط'
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>جاري التحميل...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          إعدادات الدفع
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetMethodForm()
              setMethodDialogOpen(true)
            }}
            sx={{ mr: 1 }}
          >
            إضافة طريقة دفع
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => {
              resetGatewayForm()
              setGatewayDialogOpen(true)
            }}
          >
            إعداد بوابة دفع
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PaymentIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    طرق الدفع
                  </Typography>
                  <Typography variant="h4">
                    {paymentMethods.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CreditCardIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    الطرق النشطة
                  </Typography>
                  <Typography variant="h4">
                    {paymentMethods.filter(m => m.isActive).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BankIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    بوابات الدفع
                  </Typography>
                  <Typography variant="h4">
                    {paymentGateways.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SettingsIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    البوابات النشطة
                  </Typography>
                  <Typography variant="h4">
                    {paymentGateways.filter(g => g.isActive).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Methods Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            طرق الدفع المتاحة
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>الاسم</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell>الرسوم (%)</TableCell>
                  <TableCell>الحد الأدنى</TableCell>
                  <TableCell>الحد الأقصى</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentMethods.slice((methodsPage - 1) * pageSize, methodsPage * pageSize).map((method) => (
                  <TableRow key={method.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography sx={{ mr: 1 }}>{method.icon}</Typography>
                        {method.name}
                      </Box>
                    </TableCell>
                    <TableCell>{getMethodTypeText(method.type)}</TableCell>
                    <TableCell>{method.fees}%</TableCell>
                    <TableCell>{method.minAmount} ريال</TableCell>
                    <TableCell>{method.maxAmount} ريال</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(method.isActive)}
                        color={getStatusColor(method.isActive) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="تعديل">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingMethod(method)
                            setMethodName(method.name)
                            setMethodType(method.type)
                            setMethodFees(method.fees.toString())
                            setMethodMinAmount(method.minAmount.toString())
                            setMethodMaxAmount(method.maxAmount.toString())
                            setMethodDescription(method.description)
                            setMethodIsActive(method.isActive)
                            setMethodDialogOpen(true)
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {Math.ceil(paymentMethods.length / pageSize) > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={Math.ceil(paymentMethods.length / pageSize)}
                page={methodsPage}
                onChange={(_, newPage) => setMethodsPage(newPage)}
                color="primary"
                size="large"
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Payment Gateways Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            بوابات الدفع
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>الاسم</TableCell>
                  <TableCell>المزود</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Webhook URL</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentGateways.slice((gatewaysPage - 1) * pageSize, gatewaysPage * pageSize).map((gateway) => (
                  <TableRow key={gateway.id}>
                    <TableCell>{gateway.name}</TableCell>
                    <TableCell>{gateway.provider}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {gateway.apiKey.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {gateway.webhookUrl.substring(0, 30)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(gateway.isActive)}
                        color={getStatusColor(gateway.isActive) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="تعديل">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingGateway(gateway)
                            setGatewayName(gateway.name)
                            setGatewayProvider(gateway.provider)
                            setGatewayApiKey(gateway.apiKey)
                            setGatewaySecretKey(gateway.secretKey)
                            setGatewayWebhookUrl(gateway.webhookUrl)
                            setGatewayIsActive(gateway.isActive)
                            setGatewayDialogOpen(true)
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {Math.ceil(paymentGateways.length / pageSize) > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={Math.ceil(paymentGateways.length / pageSize)}
                page={gatewaysPage}
                onChange={(_, newPage) => setGatewaysPage(newPage)}
                color="primary"
                size="large"
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Payment Method Dialog */}
      <Dialog open={methodDialogOpen} onClose={() => setMethodDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMethod ? 'تعديل طريقة الدفع' : 'إضافة طريقة دفع جديدة'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="اسم طريقة الدفع"
                  value={methodName}
                  onChange={(e) => setMethodName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="نوع طريقة الدفع"
                  select
                  value={methodType}
                  onChange={(e) => setMethodType(e.target.value as any)}
                  SelectProps={{ native: true }}
                >
                  <option value="credit_card">بطاقة ائتمان</option>
                  <option value="bank_transfer">تحويل بنكي</option>
                  <option value="wallet">محفظة إلكترونية</option>
                  <option value="cash_on_delivery">الدفع عند الاستلام</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="الرسوم (%)"
                  type="number"
                  value={methodFees}
                  onChange={(e) => setMethodFees(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="الحد الأدنى (ريال)"
                  type="number"
                  value={methodMinAmount}
                  onChange={(e) => setMethodMinAmount(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="الحد الأقصى (ريال)"
                  type="number"
                  value={methodMaxAmount}
                  onChange={(e) => setMethodMaxAmount(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="وصف طريقة الدفع"
                  multiline
                  rows={3}
                  value={methodDescription}
                  onChange={(e) => setMethodDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methodIsActive}
                      onChange={(e) => setMethodIsActive(e.target.checked)}
                    />
                  }
                  label="نشط"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMethodDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleSaveMethod} variant="contained">
            {editingMethod ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Gateway Dialog */}
      <Dialog open={gatewayDialogOpen} onClose={() => setGatewayDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGateway ? 'تعديل بوابة الدفع' : 'إضافة بوابة دفع جديدة'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="اسم البوابة"
                  value={gatewayName}
                  onChange={(e) => setGatewayName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="المزود"
                  value={gatewayProvider}
                  onChange={(e) => setGatewayProvider(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key"
                  value={gatewayApiKey}
                  onChange={(e) => setGatewayApiKey(e.target.value)}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Secret Key"
                  type="password"
                  value={gatewaySecretKey}
                  onChange={(e) => setGatewaySecretKey(e.target.value)}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={gatewayWebhookUrl}
                  onChange={(e) => setGatewayWebhookUrl(e.target.value)}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={gatewayIsActive}
                      onChange={(e) => setGatewayIsActive(e.target.checked)}
                    />
                  }
                  label="نشط"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGatewayDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleSaveGateway} variant="contained">
            {editingGateway ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}



















































